import React, { useState } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Flex,
  useColorModeValue,
  Tooltip,
} from '@chakra-ui/react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  TriangleDownIcon,
  TriangleUpIcon,
} from '@chakra-ui/icons';
import { formatCurrency } from 'utils/rag';
import type { BranchRow, BrokerRow } from './BranchBrokerTable';

// ── Types ─────────────────────────────────────────────────────────────────────

type SortKey =
  | 'broker_score'
  | 'score'
  | 'performance_score'
  | 'premium_score'
  | 'loss_ratio'
  | 'retention'
  | 'multiline_pct'
  | 'p2p_percent'
  | 'nb_premium_pl'
  | 'nb_premium_cl'
  | 'nb_premium_lf'
  | 'tbv_pl'
  | 'tbv_cl'
  | 'tbv_lf'
  | 'broker_productivity';

interface Props {
  data: BranchRow[];
  month?: string;
  onBranchClick?: (branchId: number) => void;
  onBrokerClick?: (email: string) => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function scoreCell(v: number | undefined) {
  if (!v) return '—';
  return Math.round(v).toString();
}

function pctCell(v: number | undefined) {
  if (v == null || v === 0) return '—';
  const norm = v > 1 ? v : v * 100;
  return `${norm.toFixed(1)}%`;
}

function fmtK(v: number | undefined) {
  if (!v) return '—';
  return formatCurrency(v);
}

// ── Column definitions ────────────────────────────────────────────────────────

interface Col {
  key: SortKey;
  label: string;
  render: (row: BrokerRow | BranchRow) => string;
  tooltip?: string;
}

const COLS: Col[] = [
  {
    key: 'broker_score',
    label: 'Total Broker Score',
    render: (r) => scoreCell(r.broker_score),
  },
  {
    key: 'score',
    label: 'Sales Score',
    render: (r) => scoreCell(r.score),
    tooltip: 'Composite Sales Score',
  },
  {
    key: 'performance_score',
    label: 'Service Score',
    render: () => '—',
    tooltip: 'In development',
  },
  {
    key: 'premium_score',
    label: 'Book of Business Score',
    render: () => '—',
    tooltip: 'In development',
  },
  {
    key: 'loss_ratio',
    label: 'Loss Ratio',
    render: (r) => pctCell(r.loss_ratio),
  },
  {
    key: 'retention',
    label: 'Retention Rate',
    render: (r) => pctCell(r.retention),
  },
  {
    key: 'multiline_pct',
    label: 'Multi-Line %',
    render: (r) => pctCell(r.multiline_pct),
  },
  {
    key: 'p2p_percent',
    label: 'Performance to Plan',
    render: (r) => pctCell(r.p2p_percent),
  },
  {
    key: 'nb_premium_pl',
    label: 'NB PL',
    render: (r) => fmtK(r.nb_premium_pl),
  },
  {
    key: 'nb_premium_cl',
    label: 'NB CL',
    render: (r) => fmtK(r.nb_premium_cl),
  },
  {
    key: 'nb_premium_lf',
    label: 'NB LF',
    render: (r) => fmtK(r.nb_premium_lf),
  },
  { key: 'tbv_pl', label: 'Total Book PL', render: (r) => fmtK(r.tbv_pl) },
  { key: 'tbv_cl', label: 'Total Book CL', render: (r) => fmtK(r.tbv_cl) },
  { key: 'tbv_lf', label: 'Total Book LF', render: (r) => fmtK(r.tbv_lf) },
  {
    key: 'broker_productivity',
    label: 'Broker Productivity',
    render: (r) => fmtK(r.broker_productivity),
    tooltip: 'Rolling 30-day',
  },
];

// ── Row components ────────────────────────────────────────────────────────────

interface BranchRowProps {
  branch: BranchRow;
  sortKey: SortKey;
  sortDir: 'asc' | 'desc';
  onBranchClick?: (branchId: number) => void;
  onBrokerClick?: (email: string) => void;
  colors: ReturnType<typeof useTableColors>;
}

function BranchTableRow({
  branch,
  sortKey,
  sortDir,
  onBranchClick,
  onBrokerClick,
  colors,
}: BranchRowProps) {
  const [expanded, setExpanded] = useState(false);

  const sortedBrokers = [...branch.children].sort((a, b) => {
    const va = (a[sortKey as keyof typeof a] as number) ?? 0;
    const vb = (b[sortKey as keyof typeof b] as number) ?? 0;
    return sortDir === 'desc' ? vb - va : va - vb;
  });

  function handleBranchClick() {
    setExpanded((e) => !e);
    onBranchClick?.(branch.branch_id);
  }

  return (
    <>
      <Tr
        bg={colors.branchBg}
        borderTop="1px solid"
        borderColor={colors.borderColor}
        cursor="pointer"
        _hover={{ bg: colors.hoverBg }}
        onClick={handleBranchClick}
      >
        <Td
          px="12px"
          py="8px"
          position="sticky"
          left={0}
          bg={colors.branchBg}
          zIndex={1}
          minW="140px"
        >
          <Flex align="center" gap="6px">
            {expanded ? (
              <ChevronDownIcon boxSize="3.5" color="purple.500" />
            ) : (
              <ChevronRightIcon boxSize="3.5" color="gray.400" />
            )}
            <Text fontWeight="700" fontSize="sm" color={colors.textColor}>
              {branch.label}
            </Text>
            {branch.children.length > 0 && (
              <Text fontSize="10px" color={colors.subTextColor}>
                ({branch.children.length})
              </Text>
            )}
          </Flex>
        </Td>
        {COLS.map((col) => (
          <Td
            key={col.key}
            textAlign="center"
            px="8px"
            py="8px"
            fontSize="xs"
            color={colors.textColor}
            fontWeight="500"
          >
            {col.render(branch)}
          </Td>
        ))}
      </Tr>

      {expanded &&
        sortedBrokers.map((broker) => (
          <Tr
            key={`broker-${broker.email}`}
            borderBottom="1px solid"
            borderColor={colors.borderColor}
            cursor={onBrokerClick ? 'pointer' : 'default'}
            _hover={onBrokerClick ? { bg: colors.hoverBg } : {}}
            onClick={() => onBrokerClick?.(broker.email)}
          >
            <Td
              px="12px"
              py="6px"
              position="sticky"
              left={0}
              bg={colors.rowBg}
              zIndex={1}
              minW="140px"
            >
              <Text fontSize="xs" color={colors.brokerLabelColor} pl="20px">
                {broker.label}
              </Text>
            </Td>
            {COLS.map((col) => (
              <Td
                key={col.key}
                textAlign="center"
                px="8px"
                py="6px"
                fontSize="xs"
                color={colors.textColor}
              >
                {col.render(broker)}
              </Td>
            ))}
          </Tr>
        ))}
    </>
  );
}

// ── Color hook (called once at top level) ─────────────────────────────────────

function useTableColors() {
  return {
    headerBg: useColorModeValue('gray.50', 'navy.800'),
    borderColor: useColorModeValue('gray.100', 'whiteAlpha.100'),
    branchBg: useColorModeValue('gray.50', 'navy.700'),
    rowBg: useColorModeValue('white', 'navy.800'),
    hoverBg: useColorModeValue('purple.50', 'whiteAlpha.50'),
    textColor: useColorModeValue('gray.700', 'gray.200'),
    subTextColor: useColorModeValue('gray.400', 'gray.500'),
    brokerLabelColor: useColorModeValue('gray.600', 'gray.300'),
  };
}

// ── Main component ────────────────────────────────────────────────────────────

export function OverviewPerformanceTable({
  data,
  month: _month,
  onBranchClick,
  onBrokerClick,
}: Props) {
  const [sortKey, setSortKey] = useState<SortKey>('broker_score');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const colors = useTableColors();

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  }

  const sortedBranches = [...data].sort((a, b) => {
    const va = (a[sortKey as keyof typeof a] as number) ?? 0;
    const vb = (b[sortKey as keyof typeof b] as number) ?? 0;
    return sortDir === 'desc' ? vb - va : va - vb;
  });

  if (!data.length) {
    return (
      <Text color="gray.400" fontSize="sm">
        No data available.
      </Text>
    );
  }

  return (
    <Box overflowX="auto">
      <Table size="sm" variant="unstyled">
        <Thead>
          <Tr bg={colors.headerBg}>
            <Th
              fontSize="9px"
              textTransform="uppercase"
              color={colors.subTextColor}
              fontWeight="500"
              py="6px"
              px="12px"
              minW="140px"
              position="sticky"
              left={0}
              bg={colors.headerBg}
              zIndex={1}
            >
              Sort by:
            </Th>
            {COLS.map((col) => (
              <Th
                key={col.key}
                fontSize="9px"
                textTransform="none"
                color={sortKey === col.key ? 'purple.500' : colors.subTextColor}
                fontWeight={sortKey === col.key ? '700' : '500'}
                py="6px"
                px="8px"
                textAlign="center"
                whiteSpace="normal"
                minW="72px"
                cursor="pointer"
                onClick={() => toggleSort(col.key)}
                _hover={{ color: 'purple.500' }}
              >
                <Tooltip
                  label={col.tooltip}
                  isDisabled={!col.tooltip}
                  placement="top"
                  hasArrow
                >
                  <Flex
                    align="center"
                    justify="center"
                    gap="3px"
                    direction="column"
                  >
                    <Text>{col.label}</Text>
                    {sortKey === col.key ? (
                      sortDir === 'desc' ? (
                        <TriangleDownIcon boxSize="2" color="purple.500" />
                      ) : (
                        <TriangleUpIcon boxSize="2" color="purple.500" />
                      )
                    ) : (
                      <TriangleDownIcon boxSize="2" opacity={0.3} />
                    )}
                  </Flex>
                </Tooltip>
              </Th>
            ))}
          </Tr>
        </Thead>

        <Tbody>
          {sortedBranches.map((branch) => (
            <BranchTableRow
              key={`branch-${branch.branch_id}`}
              branch={branch}
              sortKey={sortKey}
              sortDir={sortDir}
              onBranchClick={onBranchClick}
              onBrokerClick={onBrokerClick}
              colors={colors}
            />
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}
